import IconButton from "@/lib/IconButton";
import List, { Column } from "@/lib/List";
import $api from "@/lib/queries/openapi";
import { RFQListItem } from "@/lib/queries/types";
import { getUrl } from "@/lib/useToPage";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useId } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router";
import { Tooltip } from "react-tooltip";
import ArchiveIcon from "~icons/fa6-solid/box-archive";
import { datetimeFormatShort } from "../rfq/timeline/utils";
import RFQPartsTableCell from "./RFQPartsTableCell";
import RFQStatusTableCell from "./RFQStatusTableCell";

function ArchiveButton({ rfq }: { rfq: RFQListItem }) {
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();
  const tooltipId = useId();

  const { mutate: archiveRfq, isPending } = $api.useMutation(
    "post",
    "/api/teams/{team_slug}/rfqs/{id}/archive",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/teams/{team_slug}/rfqs"],
        });
        toast.success("RFQ archived");
      },
    },
  );

  return (
    <>
      <IconButton
        variant="secondary"
        type="button"
        disabled={rfq.status === "archived"}
        onClick={() =>
          archiveRfq({
            params: {
              path: {
                team_slug: teamSlug!,
                id: rfq.id,
              },
            },
          })
        }
        loading={isPending}
        data-tooltip-id={tooltipId}
        data-tooltip-content={
          rfq.status === "archived" ? "Already archived" : "Archive"
        }
        icon={<ArchiveIcon />}
      />
      {/* z-index is needed to have the tooltip above the table header */}
      <Tooltip id={tooltipId} className="z-10" />
    </>
  );
}

export default function RFQList({
  rfqs,
  isLoading,
  nonDefaultFilters,
}: {
  rfqs: RFQListItem[];
  isLoading: boolean;
  nonDefaultFilters: boolean;
}) {
  const { teamSlug } = useParams();

  return (
    <List<RFQListItem>
      columns={[
        ...columns,
        {
          content: (rfq) => <ArchiveButton rfq={rfq} />,
          skipLink: true,
          className: "w-16",
        },
      ]}
      list={rfqs}
      rowHref={(row: RFQListItem) => {
        if (row.status === "processing") return "#";
        return getUrl("rfq", {
          teamSlug: teamSlug!,
          rfqId: row.id,
        });
      }}
      isLoading={isLoading}
      emptyState={
        <div className="text-center text-gray-500 py-8">
          No RFQs found{nonDefaultFilters ? "." : " yet."}
          {nonDefaultFilters && " Try changing the filters or search criteria."}
        </div>
      }
    />
  );
}

const columns: Column<RFQListItem>[] = [
  // py-x are important here so every td has the same height and is a clickable link
  {
    label: "RFQ",
    content: ({ rfq_number }) => `#${rfq_number}`,
    className: "px-4 py-4",
  },
  {
    label: "Company",
    content: ({ contact }) => (
      <div className="font-bold capitalize">{contact.organization.name}</div>
    ),
    className: "px-4 py-4 text-nowrap",
  },
  {
    label: "Contact",
    content: ({ contact }) => contact.name,
    className: "px-4 py-4 text-nowrap",
  },
  {
    label: "Parts",
    content: (rfq) => <RFQPartsTableCell rfq={rfq} />,
    className: "px-4 py-2",
  },
  {
    label: "Status",
    content: (rfq) => <RFQStatusTableCell rfq={rfq} />,
    className: "px-4 py-4.5",
  },
  {
    label: "Date Received",
    content: ({ created_at }: RFQListItem) =>
      dayjs(created_at).format(datetimeFormatShort),
    className: "px-4  py-4 text-nowrap",
  },
];
